import moment from 'moment';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { useLazyQuery } from 'apollo-client-ppc';
import {
  FetchBillingProfilesForTransactionAccountInPpcQuery,
  FetchBillingProfilesForTransactionAccountInPpcQueryVariables,
  PaymentOnTerms,
  PaymentMethodType,
  TransactionAccount,
} from '@css-xp/graphtypes/src/generated-types-new';
import { useGetRelatedDomainsForCurrentTxA } from '@css-xp/ppc-custom-hooks';
import { downloadAsCSV } from '@css-xp/ppc-utils';
import { formatAddressLabel } from '@css-xp/base';
import { convertToMilliSeconds } from '@css-xp/base/formatted/src/date';
import {
  maxThresholdForResults,
  defaultBillingProfileSortOptions,
  SUBSCRIPTION_CONSTANTS,
} from '@css-xp/ppc-constants';
import { useFetchEccFromContactService } from '@css-xp/contact-resources';
import { EndCustomerContact } from '@atlassian/cssxp-graphtypes/src/generated-types-new';
import { fetchBillingProfilesForTransactionAccountInPPC } from '../queries/fetch-billing-profiles-for-transaction-account';
import { fetchInternalBillingProfilesForTransactionAccountInPPC } from '../queries/fetch-internal-billing-profiles-for-transaction-account';

export const getCsvHeaders = (shouldFetchInternalLicenses?: boolean) => {
  const headers = [
    'Name',
    'Sold-to address',
    'Payment method',
    shouldFetchInternalLicenses ? 'Contacts' : 'Customer contacts',
    'Currency',
    'Created on',
    'PO number',
    'Tax ID',
    'Price eligibility',
  ];

  return headers;
};

export const formatPaymentMethod = (paymentMethod: PaymentOnTerms) => {
  if (paymentMethod?.type === PaymentMethodType.CARD) {
    return paymentMethod?.type;
  }
  if (paymentMethod?.type === PaymentMethodType.DEFERRED) {
    return `Net ${paymentMethod?.terms?.invoiceDueDays}`;
  }
};

export const useBillingProfilesCSV = (
  transactionAccountId: string,
  soldToAddressIds?: string[],
  selectedTab?: string,
  enableCleanUpBillingProfile?: boolean,
  shouldFetchInternalLicenses?: boolean
  // eslint-disable-next-line max-params
) => {
  const { getECCDetailsForCSV } = useFetchEccFromContactService();
  const { relatedDomains } = useGetRelatedDomainsForCurrentTxA();

  const csvFilterOptions = soldToAddressIds
    ? [
        {
          key: 'or',
          // sending multiple ship-to ids
          value: JSON.stringify(soldToAddressIds?.map((shipToId) => ({ shipToId }))),
        },
      ]
    : [];

  if (enableCleanUpBillingProfile) {
    if (selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE) {
      csvFilterOptions.push({
        key: 'not',
        // to support old data null is also considered as Active BP status fix suggested by DE team
        value: JSON.stringify([{ status: SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE }]),
      });
    } else if (selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE) {
      csvFilterOptions.push({
        key: 'status',
        value: SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE,
      });
    }
  }
  const [loadBillingProfiles, { loading: billingProfilesLoading }] = useLazyQuery<
    FetchBillingProfilesForTransactionAccountInPpcQuery,
    FetchBillingProfilesForTransactionAccountInPpcQueryVariables
  >(
    shouldFetchInternalLicenses
      ? fetchInternalBillingProfilesForTransactionAccountInPPC
      : fetchBillingProfilesForTransactionAccountInPPC,
    {
      variables: {
        id: transactionAccountId,
        searchOptions: {
          first: maxThresholdForResults,
          offset: 0,
          sort: defaultBillingProfileSortOptions,
        },
        refetchDefaultPaymentMethod: false,
        filterOptions: shouldFetchInternalLicenses ? [] : csvFilterOptions,
        ...(shouldFetchInternalLicenses && { relatedDomains: relatedDomains }),
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const getCustomerContacts = async (invoiceGroupId: string, transactionAccountId: string) => {
    const details = await getECCDetailsForCSV(invoiceGroupId, transactionAccountId);
    const customerContacts = details?.map((detail: EndCustomerContact) => detail?.email);
    return customerContacts ? customerContacts.join(', ') : '-';
  };

  const downloadBillingProfilesCSV = async () => {
    const { data } = await loadBillingProfiles();
    const billingProfiles =
      (shouldFetchInternalLicenses
        ? (data?.transactionAccount as TransactionAccount)?.internalUseBillingProfiles?.nodes
        : data?.transactionAccount?.billingProfiles?.nodes) ?? [];

    let result: string[] = [];
    const promises = billingProfiles.map((item: any) => {
      if (item?.id && item?.transactionAccountId) {
        return getCustomerContacts(item.id, item.transactionAccountId);
      }
    });
    result = await Promise.all(promises);

    const csvData = billingProfiles.map((billingProfile: any, idx: number) => {
      const address = billingProfile?.shipToParty && formatAddressLabel(billingProfile?.shipToParty);
      const createdDate =
        billingProfile?.createdAt &&
        moment.utc(convertToMilliSeconds(billingProfile?.createdAt)).format(NEW_DATE_FORMAT);

      const csvDetails = [
        billingProfile?.name ?? '-',
        address ?? '-',
        formatPaymentMethod(billingProfile?.defaultPaymentMethod as PaymentOnTerms) ?? '-',
        result[idx] ?? '-',
        billingProfile?.currency ?? '-',
        createdDate ?? '-',
        billingProfile?.purchaseOrder?.number ?? '-',
        billingProfile?.shipToParty?.taxId ?? '-',
        billingProfile?.shipToPriceEligibility ?? '-',
      ];

      return csvDetails;
    });

    if (csvData.length) {
      downloadAsCSV({
        fileName: 'billing-profiles',
        header: getCsvHeaders(shouldFetchInternalLicenses),
        data: csvData,
      });
    }
  };

  return { downloadBillingProfilesCSV, billingProfilesLoading };
};
